import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { UseTranslationResponse, useTranslation } from 'react-i18next';
import { ws } from '../service/ws';
import Lang from '../service/lang';
import API from '../service/api';
import { VariableSizeList as List, VariableSizeList } from 'react-window';
import {
    MDBBadge,
    MDBBtn,
    MDBCheckbox,
    MDBIcon,
    MDBListGroup,
    MDBListGroupItem,
} from 'mdb-react-ui-kit';
import { TFunction } from 'i18next';

import { textColor } from 'mdb-react-ui-kit/dist/types/types/colors';
import { CustomPopover } from './popover/CustomPopover';
import { LogDataDTO, LogDTO } from '@scm/common-legacy';

const TYPE_ICON: {
    [key: string]: {
        icon: string;
        color: textColor;
        circleIconColor: textColor;
        circleColor?: string;
        circleColorClassname?: textColor;
        bgColorClassname?: textColor;
    };
} = {
    error: {
        icon: 'exclamation-triangle',
        color: 'danger',
        circleIconColor: 'white',
        circleColorClassname: 'danger',
        bgColorClassname: 'danger',
    },
    runtime: {
        icon: 'hard-drive',
        color: 'primary',
        circleIconColor: 'primary',
        circleColor: '#cdcfd1',
    },
    management: {
        icon: 'screwdriver-wrench',
        color: 'primary',
        circleIconColor: 'white',
        circleColorClassname: 'primary',
    },
    sales: {
        icon: 'coins',
        color: 'success',
        circleIconColor: 'white',
        circleColorClassname: 'success',
        bgColorClassname: 'success',
    },
    silent: {
        icon: 'hard-drive',
        color: 'primary',
        circleIconColor: 'primary',
        circleColor: '#cdcfd1',
    },

    // subtypes
    cash: {
        icon: 'coins',
        color: 'success',
        circleIconColor: 'white',
        circleColorClassname: 'success',
        bgColorClassname: 'success',
    },
    card: {
        icon: 'credit-card',
        color: 'success',
        circleIconColor: 'white',
        circleColorClassname: 'success',
        bgColorClassname: 'success',
    },
    delivery: {
        icon: 'share-square',
        color: 'success',
        circleIconColor: 'white',
        circleColorClassname: 'success',
        bgColorClassname: 'success',
    },
};

const datesDayDiffer = (date1: Date, date2: Date) => {
    return (
        date1.getFullYear() !== date2.getFullYear() ||
        date1.getMonth() !== date2.getMonth() ||
        date1.getDate() !== date2.getDate()
    );
};

const dateToText = (date: Date, t: TFunction) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
    )
        return t('today');

    if (
        date.getFullYear() === yesterday.getFullYear() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getDate() === yesterday.getDate()
    )
        return t('yesterday');

    return date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

interface ExtendedLog extends LogDTO {
    day?: string;
    height: number;
}

export interface LogViewerProps {
    groupId: number;
    locationId?: number;
    machineId?: number;
}

export const LogViewer = ({
    groupId,
    locationId,
    machineId,
}: LogViewerProps) => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState<ExtendedLog[]>([]);
    const [allowedTypes, setAllowedTypes] = useState<string[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [lang, setLang] = useState(Lang.instance.getCurrent() ?? 'en');
    const [reachedBottom, setReachedBottom] = useState(false);
    const [reachedEndOfLogs, setReachedEndOfLogs] = useState(false);
    const logContainerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<VariableSizeList>(null);
    const [containerHeight, setContainerHeight] = useState(0);
    const [typeFilter, setTypeFilter] = useState<string[] | null>(null);

    const uri = useMemo(() => {
        let uri = `/api/groups`;
        if (groupId) uri += `/${groupId}`;
        if (locationId) uri += `/${locationId}`;
        if (machineId) uri += `/${machineId}`;

        uri += `/logs?lang=${lang}`;
        if (typeFilter)
            uri += `&types=${encodeURIComponent(typeFilter.join(','))}`;
        return uri;
    }, [groupId, locationId, machineId, lang, typeFilter]);

    const onNewEvents = useCallback(
        (events: LogDTO[], atTheTop?: boolean) => {
            setLogs((prevLogs) => {
                const newEvents: ExtendedLog[] = events
                    .sort((a, b) => b.id - a.id)
                    .map((ev) => ({ ...ev, height: 0 }));

                const newLogs: ExtendedLog[] = atTheTop
                    ? [...newEvents, ...prevLogs]
                    : [...prevLogs, ...newEvents];
                const begin = 0;
                const end = newLogs.length;

                for (let i = begin; i < end; i++) {
                    if (
                        i == 0 ||
                        datesDayDiffer(
                            new Date(newLogs[i].ts),
                            new Date(newLogs[i - 1].ts),
                        )
                    ) {
                        newLogs[i].day = dateToText(new Date(newLogs[i].ts), t);
                        newLogs[i].height = 70;
                    } else if (i == newLogs.length) {
                        newLogs[i].height = 90;
                        delete newLogs[i].day;
                    } else {
                        newLogs[i].height = 40;
                        delete newLogs[i].day;
                    }
                }
                return newLogs;
            });

            if (listRef.current) {
                try {
                    listRef.current.resetAfterIndex(0);
                } catch (err) {}
            }
        },
        [listRef],
    );

    const logSizeGetter = useCallback(
        (idx: number) => {
            return logs[idx]?.height || 90;
        },
        [logs],
    );

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setLogs([]);

        try {
            const res: LogDataDTO = await API.GET(
                `${uri}${!allowedTypes ? '&getAllowedTypes=1' : ''}`,
            );
            if (Array.isArray(res)) onNewEvents(res);
            else {
                onNewEvents(res.logs);
                if (res.allowedTypes) setAllowedTypes(res.allowedTypes);
            }
        } catch (err: any) {
            console.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [uri, allowedTypes]);

    // Language change listener
    useEffect(() => {
        const langSetter = (l: string) => setLang(l);
        Lang.instance.addListener(langSetter);

        return () => {
            Lang.instance.removeListener(langSetter);
        };
    }, []);

    // Websocket logs listener
    useEffect(() => {
        ws.send('events', {
            groupId,
            locationId,
            machineId,
            language: lang,
            types: typeFilter,
        });

        const unlisten = ws.on('events', ({ event }: { event: LogDTO }) =>
            onNewEvents([event], true),
        );

        return () => {
            unlisten();
            ws.send('events', {});
        };
    }, [groupId, locationId, machineId, lang, typeFilter]);

    // Get initial logs (or reset)
    useEffect(() => {
        fetchLogs();
    }, [groupId, locationId, machineId, typeFilter]);

    // Load more logs
    useEffect(() => {
        if (
            reachedBottom &&
            !loading &&
            !reachedEndOfLogs &&
            logs.length >= 50
        ) {
            const lastId = logs.length > 0 ? logs[logs.length - 1].id : null;
            if (lastId == null) return;
            const lastOccurredTs =
                logs.length > 0 ? new Date(logs[logs.length - 1].ts) : null;
            if (lastOccurredTs == null) return;

            setLoading(true);

            API.GET(
                `${uri}&lastId=${lastId}&lastOccurredTs=${lastOccurredTs.toISOString()}`,
            )
                .then((res: LogDataDTO) => {
                    if (!Array.isArray(res)) return;

                    if (res.length == 0) setReachedEndOfLogs(true);
                    else {
                        onNewEvents(res);
                        setReachedBottom(false);
                    }
                })
                .catch((err) => {
                    console.error(err.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [loading, reachedBottom, logs, uri]);

    useEffect(() => {
        if (!logContainerRef.current) return;
        const observer = new ResizeObserver(() => {
            setContainerHeight(
                logContainerRef.current?.getBoundingClientRect().height || 0,
            );
        });
        observer.observe(logContainerRef.current);
        return () => observer.disconnect();
    }, [logContainerRef]);

    // Allowed types list
    const TypeFilterList = useMemo(
        () =>
            !typeFilter ? null : (
                <div className="d-flex flex-row flex-wrap overflow-hidden">
                    {typeFilter.map((f) => (
                        <div
                            key={f}
                            className={`d-flex flex-row align-items-center badge badge-${TYPE_ICON[f]?.color} ms-1 my-1`}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                                setTypeFilter((pre) => {
                                    const newList = pre
                                        ? pre.filter((id) => id !== f)
                                        : pre;
                                    return newList && newList.length > 0
                                        ? newList
                                        : null;
                                })
                            }
                        >
                            <MDBIcon
                                icon="times"
                                className="me-1 opacity-50"
                                color="black-50"
                            />
                            <MDBIcon
                                icon={TYPE_ICON[f]?.icon}
                                color={TYPE_ICON[f]?.color}
                            />
                            <span
                                className="ms-1"
                                style={{ userSelect: 'none' }}
                            >
                                {t(`event_type.${f}`)}
                            </span>
                        </div>
                    ))}
                </div>
            ),
        [typeFilter],
    );

    return (
        <div className="d-flex flex-column overflow-hidden flex-fill h-100 px-2">
            <div className="d-flex flex-row w-100 justify-content-end align-items-center">
                {TypeFilterList}

                {allowedTypes && (
                    <LogViewerFilter
                        typeList={allowedTypes}
                        typeFilter={typeFilter}
                        onFilter={setTypeFilter}
                    />
                )}
            </div>

            <div
                ref={logContainerRef}
                className="d-flex flex-column flex-fill overflow-auto ps-2 pe-2 h-100"
            >
                {logs.length == 0 && !loading && (
                    <span className="text-muted fst-italic w-100 text-center">
                        {t('no_logs')}
                    </span>
                )}
                {logContainerRef.current && logs && (
                    <List
                        ref={listRef}
                        height={containerHeight - 50}
                        width="100%"
                        itemCount={logs.length + (reachedEndOfLogs ? 1 : 0)}
                        itemSize={logSizeGetter}
                        onItemsRendered={({ visibleStopIndex }) => {
                            if (visibleStopIndex == logs.length - 1)
                                setReachedBottom(true);
                        }}
                    >
                        {({ index, style }) => (
                            <LogItem
                                log={logs[index]}
                                logs={logs}
                                style={style}
                                index={index}
                                groupId={groupId}
                                locationId={locationId}
                            />
                        )}
                    </List>
                )}

                {loading && (
                    <div className="d-flex flex-row w-100 justify-content-center align-items-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                                {t('loading...')}
                            </span>
                        </div>
                        ,
                    </div>
                )}
            </div>
        </div>
    );
};

const LogItem = ({
    log,
    logs,
    index,
    style,
    groupId,
    locationId,
}: {
    log?: ExtendedLog;
    logs: ExtendedLog[];
    index: number;
    style: any;
    groupId: number;
    locationId?: number;
}) => {
    const { t } = useTranslation();
    const type = useMemo(() => log?.subType ?? log?.type ?? '', [log]);
    const {
        icon,
        circleIconColor,
        circleColor,
        circleColorClassname,
        bgColorClassname,
    } = useMemo(() => TYPE_ICON[type] || { iconClassName: '' }, [type]);

    const LocationTitleView = useMemo(() => {
        // visible only when watching multiple locations
        if (!log || locationId) return null;
        const view = (
            <span
                className={`d-inline align-items-center ${
                    log.location.id ? 'fw-bold' : ''
                }`}
                style={{ lineHeight: 1 }}
            >
                <span>
                    <MDBIcon
                        icon="map-marker-alt"
                        color="primary"
                        style={{ marginRight: 2 }}
                    />
                </span>
                <span className="text-dark">{log.location.Name}</span>/
            </span>
        );
        if (log.location.id)
            return (
                <NavLink to={`/group/${groupId}/locations/${log.location.id}`}>
                    {view}
                </NavLink>
            );
        return view;
    }, [log, locationId, groupId]);

    const MachineTitleView = useMemo(() => {
        if (!log) return null;
        const ownMachine =
            (!locationId || log.machine.locationId == locationId) &&
            log.machine.groupId == groupId;

        const view = (
            <span
                className={`d-inline align-items-center`}
                style={{ lineHeight: 1 }}
            >
                {!locationId && (
                    <MDBIcon
                        icon="server"
                        fixed
                        color="primary"
                        style={{ marginRight: 2 }}
                    />
                )}
                {!ownMachine && log.machine.id && (
                    <MDBIcon
                        icon="share"
                        fixed
                        color="secondary"
                        style={{ marginRight: 2 }}
                    />
                )}
                <span
                    className={`text-dark ${ownMachine ? 'fw-bold' : ''} ${
                        log.machine.id ? '' : 'text-decoration-line-through'
                    }`}
                >
                    {log.machine.name}
                </span>
            </span>
        );

        if (log.machine.id)
            return (
                <NavLink
                    to={`/group/${log.machine.groupId}/locations/${log.machine.locationId}/machines/${log.machine.id}`}
                >
                    {view}
                </NavLink>
            );
        return view;
    }, [log, locationId, groupId]);

    if (!log)
        return (
            <span
                className="text-muted"
                style={{ textAlign: 'center', ...style }}
            >
                {t('you_reached_end')}
            </span>
        );

    return (
        <div
            className="d-flex flex-column"
            style={{ paddingLeft: 10, height: 40, ...style }}
        >
            {log.day && (
                <div
                    className="d-flex flex-row w-100 align-items-center"
                    style={{ paddingLeft: 8, height: 30 }}
                >
                    {index > 0 && (
                        <div className={'d-flex h-100 align-items-start'}>
                            <span
                                className=""
                                style={{
                                    background: 'rgba(0,0,0,0.15)',
                                    height: '100%',
                                    width: 2,
                                    zIndex: 1,
                                    position: 'relative',
                                }}
                            >
                                &nbsp;
                            </span>
                        </div>
                    )}
                    <span
                        className="flex-fill text-center text-muted "
                        style={{ marginLeft: -10 }}
                    >
                        {log.day}
                    </span>
                </div>
            )}
            <div
                className="d-flex flex-row w-100 align-items-center h-100 "
                style={{ paddingLeft: 8 }}
            >
                <div
                    className={
                        (index == logs.length - 1 ? 'd-none' : 'd-flex') +
                        ' h-100 ' +
                        (index === 0
                            ? 'align-items-end'
                            : index === logs.length - 1
                              ? 'align-items-start'
                              : '')
                    }
                >
                    <span
                        className=""
                        style={{
                            background: 'rgba(0,0,0,0.15)',
                            height: '100%',
                            width: 2,
                            zIndex: 1,
                            position: 'relative',
                        }}
                    >
                        &nbsp;
                    </span>
                </div>

                <MDBIcon
                    icon={icon}
                    color={circleIconColor}
                    size="xs"
                    className={`p-2 bg-${circleColorClassname}`}
                    style={{
                        width: 12,
                        height: 12,
                        zIndex: 200,
                        marginLeft: -15,
                        borderRadius: '16px',
                        background: circleColor,
                    }}
                />
                {/* <i className={`fas p-2 fa-xs ${iconClassName}`} style={{width:12, height:12, zIndex: 200, marginLeft: -15, borderRadius: '16px', background: iconBG || ''}} /> */}

                <div
                    className={`d-flex flex-column p-1 ms-1 w-100 bg-${bgColorClassname} bg-opacity-25`}
                    style={{ fontSize: '0.75rem', textAlign: 'start' }}
                >
                    <div
                        className="d-inline"
                        style={{ whiteSpace: 'nowrap', lineHeight: '1.3em' }}
                    >
                        {LocationTitleView}
                        {MachineTitleView}
                    </div>
                    <div
                        className="d-inline"
                        style={{ whiteSpace: 'nowrap', lineHeight: '1.3em' }}
                    >
                        <span>
                            <span
                                title={`${new Date(log.ts).toLocaleString(
                                    Lang.instance.getCurrentLocale().code,
                                )}`}
                            >
                                {new Date(log.ts).toLocaleTimeString(
                                    Lang.instance.getCurrentLocale().code,
                                )}
                                &nbsp;
                            </span>

                            <span
                                dangerouslySetInnerHTML={{ __html: log.text }}
                            ></span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LogViewerFilter = ({
    typeList,
    typeFilter,
    onFilter,
}: {
    typeList: string[];
    typeFilter: string[] | null;
    onFilter: (selectedTypes: string[] | null) => void;
}) => {
    const { t } = useTranslation();
    const [typeFilterTemp, setTypeFilterTemp] = useState(typeFilter);
    const [changed, setChanged] = useState(false);

    const allSelected = useMemo(
        () => !typeFilterTemp || typeFilterTemp.length == typeList.length,
        [typeFilterTemp, typeList],
    );

    const onAllowedTypeTempCheck = useCallback((id: string) => {
        setTypeFilterTemp((pre) => {
            return pre && pre.includes(id)
                ? pre.filter((evt) => evt !== id)
                : (pre || []).concat(id);
        });
        setChanged(true);
    }, []);

    const selectAll = useCallback(() => {
        setTypeFilterTemp(null);
        setChanged(true);
    }, []);

    const apply = useCallback(() => {
        onFilter(
            !typeFilterTemp ||
                typeFilterTemp.length == typeList.length ||
                typeFilterTemp.length == 0
                ? null
                : typeFilterTemp,
        );
    }, [typeFilterTemp, typeList]);

    useEffect(() => {
        setTypeFilterTemp(typeFilter);
    }, [typeFilter]);

    return (
        <CustomPopover
            trigger={
                <span role="button" className="text-primary">
                    <MDBIcon icon="filter" className="me-1" />
                    {t('filter')}
                </span>
            }
            btnClassName="btn-link text-primary"
            placement="left-start"
            onOpen={() => setTypeFilterTemp(typeFilter)}
            onClose={() => setTypeFilterTemp(typeFilter)}
        >
            {(close) => (
                <>
                    <MDBListGroup className="p-2">
                        <MDBListGroupItem
                            className="border-0 border-bottom p-1"
                            style={{
                                borderBottom: '2px solid rgba(0,0,0,0.2)',
                            }}
                        >
                            <MDBCheckbox
                                name="all"
                                id="all"
                                readOnly
                                checked={allSelected}
                                disabled={allSelected}
                                onClick={selectAll}
                                label={
                                    <div
                                        className={`d-flex flex-row align-items-center`}
                                    >
                                        <span
                                            className="ms-1"
                                            style={{ userSelect: 'none' }}
                                        >
                                            {t('select_all')}
                                        </span>
                                    </div>
                                }
                            />
                        </MDBListGroupItem>
                        {typeList.map((evt) => (
                            <MDBListGroupItem
                                key={evt}
                                className="border-0 p-1 d-flex flex-row align-items-center"
                            >
                                <MDBCheckbox
                                    name={evt}
                                    id={evt}
                                    checked={
                                        !typeFilterTemp ||
                                        typeFilterTemp?.includes(evt)
                                    }
                                    onChange={(e) =>
                                        onAllowedTypeTempCheck(
                                            e.currentTarget.id,
                                        )
                                    }
                                    label={
                                        <div
                                            className={`d-flex flex-row align-items-center badge badge-${TYPE_ICON[evt]?.color}`}
                                        >
                                            <MDBIcon
                                                icon={TYPE_ICON[evt]?.icon}
                                                color={TYPE_ICON[evt]?.color}
                                            />
                                            <span
                                                className="ms-1"
                                                style={{ userSelect: 'none' }}
                                            >
                                                {t(`event_type.${evt}`)}
                                            </span>
                                        </div>
                                    }
                                />
                            </MDBListGroupItem>
                        ))}
                    </MDBListGroup>
                    {changed && (
                        <div className="w-100 d-flex justify-content-center mb-2">
                            <MDBBtn
                                disabled={typeFilterTemp?.length == 0}
                                onClick={() => {
                                    apply();
                                    close();
                                }}
                                className="p-2"
                            >
                                <MDBIcon icon="filter" className="me-1" />
                                {t('apply_filters')}
                            </MDBBtn>
                        </div>
                    )}
                </>
            )}
        </CustomPopover>
    );
};
